// Anexo 2 — Atención Prehospitalaria (Fase 6, ver PLAN.md). Transcripción campo
// por campo del PDF oficial del Anexo 1 MSP (AM 00115-2021), páginas 281-282
// (footer `SNS-MSP/HCU-Anexo2/2021`), bloques A-S; spec completa en
// `docs/hce-anexos/anexo2-atencion-prehospitalaria.md`.
//
// Criterios de siempre: equivalencia funcional (no se dibuja la hoja), nada de
// contenido clínico que el papel no imprime, FHIR solo con consumidor real
// (signos vitales → `Observation`; un `Condition` por diagnóstico de F), el
// resto `Par[]` de respaldo en el PDF firmado.
//
// Decisiones donde el papel no dice (todas conservadoras):
// - Glasgow: el papel imprime ocular/verbal/motor y `TOTAL /15` sin puntajes por
//   opción. Se capturan los 3 componentes como número (1-4 / 1-5 / 1-6, los del
//   Glasgow estándar, los mismos que ya usa 008) y el total lo calcula el código;
//   sin interpretación. RTS (`/12`): el papel no imprime la tabla de codificación
//   ni las bandas, así que ECG/TAS/FR/total se capturan tal cual (sin cálculo).
// - Examen pupilar (D): el papel lo dibuja a la derecha de las 3 tomas; se
//   captura una sola vez (global), no por toma.
// - Tabla de medicación (G): el papel no numera ni permite contar los renglones;
//   se dejan 6 filas.
// - Localización de lesiones (H): el papel dibuja 4 vistas del cuerpo sin
//   regiones nombradas; la equivalencia funcional es una lista de lesiones (tipo
//   1-15 de la leyenda impresa + vista + zona en texto libre).
import { diagnosticoConMarcaVacio } from './MarcaDiagnostico';
import {
  conCodigoCie,
  OPCIONES_SI_NO,
  paresDeCampos,
  textoDeCampo,
  valoresVacios,
  type CampoSimple,
  type OpcionCampo,
  type Valores,
} from './camposSimplesDatos';
import type { DiagnosticoCie10Valor } from './SelectorDiagnosticoCie10';
import type { Par } from './index';
import { estadoInicialTablaConColumnas, resumenFilaTablaConColumnas, type ColumnaTablaConColumnas, type EstadoTablaConColumnas, type FilaTablaConColumnas } from './uci/TablaConColumnas';

const casilla = (clave: string, etiqueta: string): CampoSimple => ({ clave, etiqueta, tipo: 'casilla' });
const numero = (clave: string, etiqueta: string): CampoSimple => ({ clave, etiqueta, tipo: 'numero' });
const opciones = (...etiquetas: readonly string[]): readonly OpcionCampo[] => etiquetas.map((e) => ({ valor: e, etiqueta: e }));
const desde = (min: number, max: number): readonly OpcionCampo[] => Array.from({ length: max - min + 1 }, (_, i) => ({ valor: String(min + i), etiqueta: String(min + i) }));

/** Títulos de bloque como los imprime la hoja. */
export const TITULOS_ANEXO2 = {
  ambulancia: 'A. Datos de la ambulancia',
  generales: 'B. Datos generales',
  interrogatorio: 'C. Interrogatorio',
  constantes: 'D. Constantes vitales',
  examen: 'E. Examen físico',
  diagnostico: 'F. Diagnóstico presuntivo',
  procedimientos: 'G. Procedimientos',
  trauma: 'H. Trauma',
  gineco: 'I. Emergencia gineco-obstétrica y neonatal',
  paro: 'J. Paro cardio respiratorio',
  estabilizacion: 'K. Estabilización en establecimiento de salud',
  custodia: 'L. Custodia de pertenencias',
  descargo: 'M. Descargo de medicamentos y dispositivos médicos',
  observaciones: 'N. Observaciones',
  acompanante: 'O. Datos familiares / acompañantes',
  equipo: 'P. Equipo de respuesta de la unidad',
  radio: 'Q. Radio despacho',
  destino: 'R. Datos de destino del paciente',
  exoneracion: 'S. Exoneración de responsabilidad',
} as const;

// ---------- A. Datos de la ambulancia ----------

export const CAMPOS_AMBULANCIA_ANEXO2: readonly CampoSimple[] = [
  { clave: 'entidad', etiqueta: 'Entidad del sistema' },
  { clave: 'baseFisica', etiqueta: 'Base física' },
  { clave: 'unicodigo', etiqueta: 'Unicódigo' },
  { clave: 'numeroAmbulancia', etiqueta: 'N° ambulancia' },
  { clave: 'tipologia', etiqueta: 'Tipología' },
  { clave: 'zona', etiqueta: 'Zona' },
];

// ---------- B. Datos generales ----------

export const CAMPOS_TIPO_ATENCION_ANEXO2: readonly CampoSimple[] = [
  casilla('trauma', 'Trauma'),
  casilla('obstetrico', 'Obstétrico'),
  casilla('clinica', 'Clínica'),
  casilla('saludMental', 'Salud mental'),
];

export const CAMPOS_GENERALES_ANEXO2: readonly CampoSimple[] = [
  { clave: 'tipoDocumento', etiqueta: 'Tipo de documento de identificación', tipo: 'select', opciones: opciones('Cédula', 'Pasaporte', 'Carnet de refugiado', 'Sin identificación') },
  { clave: 'numeroDocumento', etiqueta: 'Número de documento de identificación' },
  { clave: 'primerApellido', etiqueta: 'Primer apellido' },
  { clave: 'segundoApellido', etiqueta: 'Segundo apellido' },
  { clave: 'primerNombre', etiqueta: 'Primer nombre' },
  { clave: 'segundoNombre', etiqueta: 'Segundo nombre' },
  // El instructivo: "M si es mujer o H si es hombre, según indique el paciente".
  {
    clave: 'sexo',
    etiqueta: 'Sexo',
    tipo: 'select',
    opciones: [
      { valor: 'M', etiqueta: 'M — Mujer' },
      { valor: 'H', etiqueta: 'H — Hombre' },
    ],
  },
  numero('edad', 'Edad'),
  { clave: 'condicionEdad', etiqueta: 'Condición de la edad', tipo: 'select', opciones: opciones('Horas', 'Días', 'Meses', 'Años', 'Ignorado') },
  { clave: 'fechaDespacho', etiqueta: 'Fecha despacho', tipo: 'fecha' },
  { clave: 'horaDespacho', etiqueta: 'Hora despacho', tipo: 'hora' },
  { clave: 'direccionEvento', etiqueta: 'Dirección del evento' },
  { clave: 'escenarioEvento', etiqueta: 'Escenario del evento' },
  { clave: 'fechaAtencion', etiqueta: 'Fecha atención', tipo: 'fecha' },
  { clave: 'horaAtencion', etiqueta: 'Hora atención', tipo: 'hora' },
  { clave: 'tipoSeguro', etiqueta: 'Tipo de seguro', tipo: 'select', opciones: opciones('ISSFA', 'ISSPOL', 'IESS', 'Ninguno', 'Seguro privado') },
  { clave: 'seguroDescripcion', etiqueta: 'Tipo de seguro — describa' },
  casilla('sppat', 'Sistema público para pago de accidentes de tránsito (SPPAT)'),
  { clave: 'placaVehiculo', etiqueta: 'Placa del vehículo' },
];

// ---------- C. Interrogatorio ----------

export const CAMPOS_INTERROGATORIO_ANEXO2: readonly CampoSimple[] = [
  casilla('alergias', 'Alergias'),
  { clave: 'alergiasDetalle', etiqueta: 'Alergias — cuál' },
  casilla('discAuditiva', 'Discapacidad auditiva'),
  casilla('discVisual', 'Discapacidad visual'),
  casilla('discFisica', 'Discapacidad física'),
  casilla('discIntelectual', 'Discapacidad intelectual'),
  casilla('discPsicosocial', 'Discapacidad psicosocial'),
  { clave: 'texto', etiqueta: 'Antecedentes del evento, cinemática, síntomas, medicamentos que recibe, antecedentes patológicos personales, adicciones', tipo: 'area' },
];

// ---------- D. Constantes vitales (3 tomas + examen pupilar) ----------

export const NUM_TOMAS_ANEXO2 = 3;

export const CAMPOS_TOMA_ANEXO2: readonly CampoSimple[] = [
  { clave: 'hora', etiqueta: 'Hora', tipo: 'hora' },
  numero('fc', 'FC/min'),
  numero('temperatura', 'Temperatura °C'),
  numero('paSistolica', 'Tensión arterial sistólica (mm/Hg)'),
  numero('paDiastolica', 'Tensión arterial diastólica (mm/Hg)'),
  numero('fr', 'FR/min'),
  numero('spo2', 'SpO2 (%)'),
  numero('glucosa', 'Glucosa mg/dl'),
  { clave: 'glasgowOcular', etiqueta: 'Glasgow — ocular', tipo: 'select', opciones: desde(1, 4) },
  { clave: 'glasgowVerbal', etiqueta: 'Glasgow — verbal', tipo: 'select', opciones: desde(1, 5) },
  { clave: 'glasgowMotor', etiqueta: 'Glasgow — motor', tipo: 'select', opciones: desde(1, 6) },
  numero('rtsEcg', 'RTS — ECG'),
  numero('rtsTas', 'RTS — TAS'),
  numero('rtsFr', 'RTS — FR'),
  numero('rtsTotal', 'RTS — total (/12)'),
];

/** Total de Glasgow (/15) — `null` mientras falte alguno de los 3 componentes (no hay total parcial). */
export function totalGlasgowAnexo2(toma: Valores): number | null {
  const crudos = [toma.glasgowOcular, toma.glasgowVerbal, toma.glasgowMotor];
  if (crudos.some((v) => v === undefined || v === '')) return null;
  return crudos.reduce((suma, v) => suma + Number(v), 0);
}

export const CAMPOS_PUPILAS_ANEXO2: readonly CampoSimple[] = [
  { clave: 'reactividadOd', etiqueta: 'Reactividad OD', tipo: 'select', opciones: OPCIONES_SI_NO },
  { clave: 'reactividadOi', etiqueta: 'Reactividad OI', tipo: 'select', opciones: OPCIONES_SI_NO },
  { clave: 'simetria', etiqueta: 'Pupilas', tipo: 'select', opciones: opciones('Isocoria', 'Anisocoria') },
  { clave: 'diametro', etiqueta: 'Pupilas — tamaño', tipo: 'select', opciones: opciones('Miosis', 'Midriasis') },
];

// ---------- E. Examen físico ----------

export const CAMPOS_EXAMEN_ANEXO2: readonly CampoSimple[] = [
  casilla('cara', '1. Cara'),
  casilla('craneo', '2. Cráneo'),
  casilla('cuello', '3. Cuello'),
  casilla('torax', '4. Tórax'),
  casilla('abdomen', '5. Abdomen'),
  casilla('pelvis', '6. Pelvis, ingle, periné'),
  casilla('columna', '7. Columna'),
  casilla('extremidadesSuperiores', '8. Extremidades superiores'),
  casilla('extremidadesInferiores', '9. Extremidades inferiores'),
  casilla('sinPatologia', '10. Sin patología aparente'),
  { clave: 'descripcion', etiqueta: 'Descripción', tipo: 'area' },
];

// ---------- F. Diagnóstico presuntivo (solo CIE-10, 2 filas — sin PRE/DEF en el papel) ----------

export const NUM_DIAGNOSTICOS_ANEXO2 = 2;

// ---------- G. Procedimientos ----------

export const CAMPOS_VIA_AEREA_ANEXO2: readonly CampoSimple[] = [
  casilla('manual', 'Manual'),
  casilla('canulaOf', 'Cánula O.F.'),
  casilla('canulaNf', 'Cánula N.F.'),
  casilla('supraglotico', 'Dispositivo supraglótico'),
  casilla('intubacionOt', 'Intubación orotraqueal'),
  casilla('puncionCricotiroidea', 'Punción cricotiroidea'),
];

export const CAMPOS_VTP_ANEXO2: readonly CampoSimple[] = [
  casilla('ventilacionManual', 'Ventilación manual'),
  casilla('canulaNasal', 'Cánula nasal'),
  casilla('mascarilla', 'Mascarilla'),
  casilla('bvm', 'BVM'),
  numero('o2', 'O2 (lt/min)'),
  casilla('ventilacionMecanica', 'Ventilación mecánica'),
];

export const CAMPOS_MODO_VENTILATORIO_ANEXO2: readonly CampoSimple[] = [
  casilla('asistido', 'Asistido'),
  casilla('espontaneo', 'Espontáneo'),
  { clave: 'peep', etiqueta: 'PEEP' },
  { clave: 'volumenTidal', etiqueta: 'Volumen tidal' },
  { clave: 'fr', etiqueta: 'FR' },
  { clave: 'fio2', etiqueta: 'FiO2' },
];

export const CAMPOS_HEMORRAGIA_ANEXO2: readonly CampoSimple[] = [
  casilla('presionDirecta', 'Presión directa'),
  casilla('vendajeCompresivo', 'Vendaje compresivo'),
  casilla('torniquete', 'Torniquete'),
  { clave: 'horaAplicacion', etiqueta: 'Hora de aplicación', tipo: 'hora' },
];

export const CAMPOS_CANALIZACION_ANEXO2: readonly CampoSimple[] = [
  casilla('intravenoso', 'Intravenoso'),
  casilla('intraoseo', 'Intraóseo'),
  { clave: 'cateter', etiqueta: 'Catéter N°' },
];

export const CAMPOS_INMOVILIZACION_ANEXO2: readonly CampoSimple[] = [
  casilla('collarCervical', 'Collar cervical'),
  casilla('ferulasRigidas', 'Férulas rígidas'),
  casilla('fel', 'F.E.L.'),
  casilla('chalecoExtricacion', 'Chaleco de extricación'),
  casilla('ferulaTraccion', 'Férula de tracción'),
];

export const CAMPOS_OTROS_PROCEDIMIENTOS_ANEXO2: readonly CampoSimple[] = [
  casilla('descompresionToracica', 'Descompresión torácica'),
  casilla('sondajeVesical', 'Sondaje vesical'),
  casilla('lavadoGastrico', 'Lavado gástrico'),
  casilla('ekg', 'EKG'),
  { clave: 'otros', etiqueta: 'Otros procedimientos', tipo: 'area' },
];

/** Grupos de G en el orden impreso: (título del grupo, campos, clave de estado). */
export const GRUPOS_PROCEDIMIENTOS_ANEXO2 = [
  { clave: 'viaAerea', titulo: 'Vía aérea', campos: CAMPOS_VIA_AEREA_ANEXO2, columnas: 3 },
  { clave: 'vtp', titulo: 'V.T.P.', campos: CAMPOS_VTP_ANEXO2, columnas: 3 },
  { clave: 'modoVentilatorio', titulo: 'Modo ventilatorio', campos: CAMPOS_MODO_VENTILATORIO_ANEXO2, columnas: 3 },
  { clave: 'hemorragia', titulo: 'Control de hemorragia', campos: CAMPOS_HEMORRAGIA_ANEXO2, columnas: 4 },
  { clave: 'canalizacion', titulo: 'Canalización de vías', campos: CAMPOS_CANALIZACION_ANEXO2, columnas: 3 },
  { clave: 'inmovilizacion', titulo: 'Inmovilización', campos: CAMPOS_INMOVILIZACION_ANEXO2, columnas: 3 },
  { clave: 'otrosProcedimientos', titulo: 'Otros procedimientos', campos: CAMPOS_OTROS_PROCEDIMIENTOS_ANEXO2, columnas: 4 },
] as const;

export type ClaveGrupoProcedimientos = (typeof GRUPOS_PROCEDIMIENTOS_ANEXO2)[number]['clave'];

export const NUM_FILAS_MEDICACION_ANEXO2 = 6;
export const FILAS_MEDICACION_ANEXO2: readonly FilaTablaConColumnas[] = Array.from({ length: NUM_FILAS_MEDICACION_ANEXO2 }, (_, i) => ({ clave: `m${i + 1}`, etiqueta: String(i + 1) }));
export const COLUMNAS_MEDICACION_ANEXO2: readonly ColumnaTablaConColumnas[] = [
  { clave: 'autorizadoPor', etiqueta: 'Autorizado por' },
  { clave: 'medicacion', etiqueta: 'Medicación' },
  { clave: 'dosis', etiqueta: 'Dosis' },
  { clave: 'via', etiqueta: 'Vía de administración' },
  { clave: 'hora', etiqueta: 'Hora' },
];

// ---------- H. Trauma ----------

export const CAMPOS_ORIGEN_TRAUMA_ANEXO2: readonly CampoSimple[] = [casilla('violencia', 'Violencia'), casilla('accidente', 'Accidente'), casilla('autoagresion', 'Autoagresión')];

export const CAMPOS_TRANSITO_ANEXO2: readonly CampoSimple[] = [
  casilla('vehicular', 'Accidente vehicular'),
  casilla('moto', 'Accidente moto'),
  casilla('bicicleta', 'Accidente bicicleta'),
  { clave: 'otro', etiqueta: 'Otro' },
  casilla('conductor', 'Víctima conductor'),
  casilla('pasajero', 'Víctima pasajero'),
  casilla('asientoDelantero', 'Asiento delantero'),
  casilla('asientoTrasero', 'Asiento trasero'),
  casilla('eyectado', 'Eyectado del vehículo'),
  casilla('caidoVehiculo', 'Caído del vehículo'),
  casilla('peaton', 'Víctima peatón'),
  casilla('atropello', 'Atropello'),
  casilla('impactoFrontal', 'Impacto frontal'),
  casilla('impactoLateral', 'Impacto lateral'),
  casilla('impactoPosterior', 'Impacto posterior'),
  casilla('volcamiento', 'Volcamiento'),
  casilla('atrapado', 'Atrapado'),
  numero('tiempoRescate', 'Tiempo de rescate (min)'),
  casilla('airbag', 'Airbag activado'),
  casilla('cinturon', 'Cinturón colocado'),
  casilla('casco', 'Casco colocado'),
  casilla('chalecoAirbag', 'Chaleco airbag'),
  casilla('ropaProteccion', 'Ropa de protección'),
];

export const CAMPOS_OTROS_TRAUMA_ANEXO2: readonly CampoSimple[] = [
  casilla('agresionFisica', 'Agresión física'),
  casilla('agresionIntrafamiliar', 'Agresión intrafamiliar'),
  casilla('agresionSexual', 'Agresión sexual'),
  casilla('cuerpoExtrano', 'Cuerpo extraño'),
  casilla('sumersion', 'Sumersión'),
  casilla('explosion', 'Explosión'),
  casilla('aplastamiento', 'Aplastamiento'),
  casilla('electrocucion', 'Electrocución'),
  casilla('armaFuego', 'Arma de fuego'),
  casilla('armaCortante', 'Arma cortante'),
  casilla('armaPunzante', 'Arma punzante'),
  casilla('amputacion', 'Amputación'),
  { clave: 'otrosEspecifique', etiqueta: 'Otros — especifique' },
  { clave: 'caidaAltura', etiqueta: 'Caída (altura aprox.)' },
];

/** Leyenda impresa de lesiones, numeradas 1-15. */
export const LESIONES_ANEXO2: readonly OpcionCampo[] = [
  'Herida penetrante',
  'Herida no penetrante',
  'Herida cortante',
  'Esguince',
  'Luxación',
  'Fractura expuesta',
  'Fractura cerrada',
  'Hematoma',
  'Amputación',
  'Objeto empalado',
  'Mordedura',
  'Cuerpo extraño',
  'Quemadura',
  'Contusión',
  'Dolor',
].map((nombre, i) => ({ valor: String(i + 1), etiqueta: `${i + 1}. ${nombre}` }));

export const VISTAS_ANEXO2: readonly OpcionCampo[] = opciones('Frontal', 'Posterior', 'Lateral derecha', 'Lateral izquierda');

export const CAMPOS_LESION_ANEXO2: readonly CampoSimple[] = [
  { clave: 'lesion', etiqueta: 'Lesión', tipo: 'select', opciones: LESIONES_ANEXO2 },
  { clave: 'vista', etiqueta: 'Vista', tipo: 'select', opciones: VISTAS_ANEXO2 },
  { clave: 'zona', etiqueta: 'Zona afectada' },
];

export const MAX_LESIONES_ANEXO2 = 20;

// ---------- I. Emergencia gineco-obstétrica y neonatal ----------

export const CAMPOS_GINECO_ANEXO2: readonly CampoSimple[] = [
  casilla('parto', 'Parto'),
  casilla('aborto', 'Aborto'),
  { clave: 'fum', etiqueta: 'FUM', tipo: 'fecha' },
  numero('semanasGestacion', 'Semanas de gestación'),
  numero('gestas', 'Gestas'),
  numero('partos', 'Partos'),
  numero('abortos', 'Abortos'),
  numero('cesareas', 'Cesáreas'),
  casilla('expulsivo', 'Expulsivo'),
  casilla('membranasIntegras', 'Membranas íntegras'),
  casilla('membranasRotas', 'Membranas rotas'),
  { clave: 'tiempoRuptura', etiqueta: 'Tiempo de ruptura' },
  { clave: 'movimientoFetal', etiqueta: 'Movimiento fetal', tipo: 'select', opciones: OPCIONES_SI_NO },
  numero('frecuenciaCardiacaFetal', 'Frecuencia cardíaca fetal'),
  // El papel no imprime la escala de MAMA: se captura el valor tal cual.
  { clave: 'scoreMama', etiqueta: 'Score MAMA' },
  { clave: 'presentacion', etiqueta: 'Presentación' },
  { clave: 'dilatacion', etiqueta: 'Dilatación' },
  { clave: 'borramiento', etiqueta: 'Borramiento' },
  { clave: 'plano', etiqueta: 'Plano' },
  { clave: 'alturaUterina', etiqueta: 'Altura uterina' },
  { clave: 'fechaNacimiento', etiqueta: 'Fecha de nacimiento', tipo: 'fecha' },
  { clave: 'horaNacimiento', etiqueta: 'Hora del nacimiento', tipo: 'hora' },
  // Apgar: el papel imprime solo "/10" por minuto, sin componentes ni interpretación.
  numero('apgar1', 'Apgar 1 minuto (/10)'),
  numero('apgar5', 'Apgar 5 minutos (/10)'),
  { clave: 'sexoRn', etiqueta: 'Sexo R.N.' },
];

/** Contracciones uterinas: 3 renglones sin numerar; el papel no imprime unidades. */
export const FILAS_CONTRACCIONES_ANEXO2: readonly FilaTablaConColumnas[] = [1, 2, 3].map((n) => ({ clave: `c${n}`, etiqueta: String(n) }));
export const COLUMNAS_CONTRACCIONES_ANEXO2: readonly ColumnaTablaConColumnas[] = [
  { clave: 'hora', etiqueta: 'Hora' },
  { clave: 'duracion', etiqueta: 'Duración' },
  { clave: 'frecuencia', etiqueta: 'Frecuencia' },
];

// ---------- J. Paro cardio respiratorio ----------

export const CAMPOS_PARO_ANEXO2: readonly CampoSimple[] = [
  casilla('presenciado', 'Presenciado'),
  casilla('noPresenciado', 'No presenciado'),
  casilla('rcpEntrenado', 'RCP por personal entrenado'),
  casilla('rcpLego', 'RCP por lego'),
  casilla('rcpViaPublica', 'RCP en vía pública'),
  casilla('rcpAmbulancia', 'RCP en ambulancia'),
  casilla('terapiaElectrica', 'Terapia eléctrica'),
  casilla('medicacion', 'Medicación'),
  { clave: 'tiempoRcpPrevio', etiqueta: 'Tiempo aproximado de RCP previo al arribo del personal sanitario' },
  { clave: 'horaInicioRcp', etiqueta: 'Hora inicio RCP', tipo: 'hora' },
  { clave: 'horaFinalRcp', etiqueta: 'Hora final RCP', tipo: 'hora' },
  casilla('rcpBasico', 'RCP básico'),
  casilla('rcpAvanzado', 'RCP avanzado'),
  { clave: 'observaciones', etiqueta: 'Observaciones' },
];

// ---------- K. Estabilización / L. Custodia ----------
// Las firmas y sellos del papel no se capturan como campo: el documento se firma
// electrónicamente (PAdES) por quien lo guarda.

export const CAMPOS_ESTABILIZACION_ANEXO2: readonly CampoSimple[] = [
  { clave: 'establecimiento', etiqueta: 'Nombre del establecimiento' },
  { clave: 'servicio', etiqueta: 'Servicio' },
  { clave: 'fecha', etiqueta: 'Fecha', tipo: 'fecha' },
  { clave: 'hora', etiqueta: 'Hora', tipo: 'hora' },
  { clave: 'apellidos', etiqueta: 'Quien estabiliza — apellidos' },
  { clave: 'nombres', etiqueta: 'Quien estabiliza — nombres' },
  { clave: 'cargo', etiqueta: 'Quien estabiliza — cargo' },
  { clave: 'causa', etiqueta: 'Causa', tipo: 'area' },
];

// En "entrega" el papel imprime Apellidos-Nombres; en "recibe", Nombres-Apellidos.
export const CAMPOS_CUSTODIA_ANEXO2: readonly CampoSimple[] = [
  { clave: 'entregaApellidos', etiqueta: 'Quien entrega — apellidos' },
  { clave: 'entregaNombres', etiqueta: 'Quien entrega — nombres' },
  { clave: 'entregaCargo', etiqueta: 'Quien entrega — cargo' },
  { clave: 'recibeNombres', etiqueta: 'Quien recibe — nombres' },
  { clave: 'recibeApellidos', etiqueta: 'Quien recibe — apellidos' },
  { clave: 'recibeCargo', etiqueta: 'Quien recibe — cargo / parentesco' },
  { clave: 'descripcion', etiqueta: 'Descripción', tipo: 'area' },
];

// ---------- M. Descargo de medicamentos y dispositivos médicos (12 filas) ----------

export const FILAS_DESCARGO_ANEXO2: readonly FilaTablaConColumnas[] = Array.from({ length: 12 }, (_, i) => ({ clave: `d${i + 1}`, etiqueta: String(i + 1) }));
export const COLUMNAS_DESCARGO_ANEXO2: readonly ColumnaTablaConColumnas[] = [
  { clave: 'medicamento', etiqueta: 'Medicamento / dispositivo' },
  { clave: 'presentacion', etiqueta: 'Presentación' },
  { clave: 'consumo', etiqueta: 'Consumo' },
];

// ---------- N. Observaciones ----------

export const CAMPOS_OBSERVACIONES_ANEXO2: readonly CampoSimple[] = [{ clave: 'texto', etiqueta: 'Observaciones', tipo: 'area' }];

// ---------- O. Datos familiares / acompañantes ----------

export const CAMPOS_ACOMPANANTE_ANEXO2: readonly CampoSimple[] = [
  { clave: 'tipoDocumento', etiqueta: 'Tipo de documento de identificación', tipo: 'select', opciones: opciones('Cédula', 'Pasaporte', 'Carnet de refugiado', 'Sin identificación') },
  { clave: 'numeroDocumento', etiqueta: 'Número de documento de identificación' },
  { clave: 'primerApellido', etiqueta: 'Primer apellido' },
  { clave: 'segundoApellido', etiqueta: 'Segundo apellido' },
  { clave: 'primerNombre', etiqueta: 'Primer nombre' },
  { clave: 'segundoNombre', etiqueta: 'Segundo nombre' },
  { clave: 'parentesco', etiqueta: 'Parentesco' },
  { clave: 'telefono', etiqueta: 'Número telefónico de contacto' },
];

// ---------- P. Equipo de respuesta / Q. Radio despacho (cargos preimpresos) ----------

export const FILAS_EQUIPO_ANEXO2: readonly FilaTablaConColumnas[] = [
  { clave: 'operador', etiqueta: 'Operador V. Emergencia' },
  { clave: 'profesional1', etiqueta: 'Profesional de salud 1' },
  { clave: 'profesional2', etiqueta: 'Profesional de salud 2' },
  { clave: 'profesional3', etiqueta: 'Profesional de salud 3' },
];
export const FILAS_RADIO_ANEXO2: readonly FilaTablaConColumnas[] = [
  { clave: 'radioDespachador', etiqueta: 'Radio despachador' },
  { clave: 'evaluador', etiqueta: 'Evaluador prehospitalario' },
];
export const COLUMNAS_PERSONAL_ANEXO2: readonly ColumnaTablaConColumnas[] = [
  { clave: 'apellidos', etiqueta: 'Apellidos' },
  { clave: 'nombres', etiqueta: 'Nombres' },
];

// Datos de la unidad: la hoja imprime una fila de encabezados (SALIDA DE BASE / LLEGADA A BASE /
// HORA EN BASE/ CLAVE 1, este último cortado por el borde del escaneo) y una sola fila de datos
// preimpresa "KILOMETRAJE" con 3 celdas: un valor por columna. Sin unidades impresas.
export const CAMPOS_UNIDAD_ANEXO2: readonly CampoSimple[] = [
  { clave: 'kmSalidaBase', etiqueta: 'Kilometraje — salida de base' },
  { clave: 'kmLlegadaBase', etiqueta: 'Kilometraje — llegada a base' },
  { clave: 'kmHoraEnBase', etiqueta: 'Kilometraje — hora en base / clave 1' },
];

// ---------- R. Datos de destino del paciente ----------

export const CAMPOS_DESTINO_ANEXO2: readonly CampoSimple[] = [
  casilla('presenciaSignos', 'Presencia de signos vitales'),
  casilla('ausenciaSignos', 'Ausencia de signos vitales'),
  casilla('atendidoLugar', 'Atendido en el lugar'),
  { clave: 'ameritaTraslado', etiqueta: 'Amerita traslado', tipo: 'select', opciones: OPCIONES_SI_NO },
  casilla('trasladoEstablecimiento', 'Traslado a establecimiento de salud'),
  { clave: 'establecimientoRecibe', etiqueta: 'Nombre del establecimiento de salud que recibe' },
  { clave: 'horaLlegada', etiqueta: 'Hora de llegada al establecimiento de salud', tipo: 'hora' },
  { clave: 'horaFirma', etiqueta: 'Hora de firma', tipo: 'hora' },
  { clave: 'recibeApellidos', etiqueta: 'Quien recibe — apellidos' },
  { clave: 'recibeNombres', etiqueta: 'Quien recibe — nombres' },
  { clave: 'recibeCargo', etiqueta: 'Quien recibe — cargo' },
  { clave: 'entregaApellidos', etiqueta: 'Quien entrega — apellidos' },
  { clave: 'entregaNombres', etiqueta: 'Quien entrega — nombres' },
  { clave: 'entregaCargo', etiqueta: 'Quien entrega — cargo' },
];

// ---------- S. Exoneración de responsabilidad ----------

export const CAMPOS_EXONERACION_ANEXO2: readonly CampoSimple[] = [
  casilla('rehusaAtencion', 'Rehúsa atención'),
  casilla('rehusaTraslado', 'Rehúsa traslado'),
  casilla('rehusaTratamiento', 'Rehúsa tratamiento'),
  { clave: 'apellidos', etiqueta: 'Apellidos' },
  { clave: 'nombres', etiqueta: 'Nombres' },
  { clave: 'numeroDocumento', etiqueta: 'Número de documento de identidad' },
  { clave: 'observaciones', etiqueta: 'Observaciones', tipo: 'area' },
];

// ---------- Estado ----------

export interface EstadoAnexo2 {
  readonly gineco: Valores;
  readonly contracciones: EstadoTablaConColumnas;
  readonly paro: Valores;
  readonly estabilizacion: Valores;
  readonly custodia: Valores;
  readonly descargo: EstadoTablaConColumnas;
  readonly observaciones: Valores;
  readonly acompanante: Valores;
  readonly equipo: EstadoTablaConColumnas;
  readonly radio: EstadoTablaConColumnas;
  readonly unidad: Valores;
  readonly destino: Valores;
  readonly exoneracion: Valores;
  readonly ambulancia: Valores;
  readonly tipoAtencion: Valores;
  readonly generales: Valores;
  readonly interrogatorio: Valores;
  readonly tomas: readonly Valores[];
  readonly pupilas: Valores;
  readonly examen: Valores;
  readonly diagnosticos: readonly DiagnosticoCie10Valor[];
  readonly procedimientos: Readonly<Record<ClaveGrupoProcedimientos, Valores>>;
  readonly medicacion: EstadoTablaConColumnas;
  readonly origenTrauma: Valores;
  readonly transito: Valores;
  readonly otrosTrauma: Valores;
  readonly lesiones: readonly Valores[];
}

export function estadoInicialAnexo2(): EstadoAnexo2 {
  return {
    ambulancia: valoresVacios(CAMPOS_AMBULANCIA_ANEXO2),
    tipoAtencion: valoresVacios(CAMPOS_TIPO_ATENCION_ANEXO2),
    generales: valoresVacios(CAMPOS_GENERALES_ANEXO2),
    interrogatorio: valoresVacios(CAMPOS_INTERROGATORIO_ANEXO2),
    tomas: Array.from({ length: NUM_TOMAS_ANEXO2 }, () => valoresVacios(CAMPOS_TOMA_ANEXO2)),
    pupilas: valoresVacios(CAMPOS_PUPILAS_ANEXO2),
    examen: valoresVacios(CAMPOS_EXAMEN_ANEXO2),
    diagnosticos: Array.from({ length: NUM_DIAGNOSTICOS_ANEXO2 }, () => diagnosticoConMarcaVacio().diagnostico),
    procedimientos: Object.fromEntries(GRUPOS_PROCEDIMIENTOS_ANEXO2.map((g) => [g.clave, valoresVacios(g.campos)])) as Record<ClaveGrupoProcedimientos, Valores>,
    medicacion: estadoInicialTablaConColumnas(FILAS_MEDICACION_ANEXO2, COLUMNAS_MEDICACION_ANEXO2),
    origenTrauma: valoresVacios(CAMPOS_ORIGEN_TRAUMA_ANEXO2),
    transito: valoresVacios(CAMPOS_TRANSITO_ANEXO2),
    otrosTrauma: valoresVacios(CAMPOS_OTROS_TRAUMA_ANEXO2),
    lesiones: [],
    gineco: valoresVacios(CAMPOS_GINECO_ANEXO2),
    contracciones: estadoInicialTablaConColumnas(FILAS_CONTRACCIONES_ANEXO2, COLUMNAS_CONTRACCIONES_ANEXO2),
    paro: valoresVacios(CAMPOS_PARO_ANEXO2),
    estabilizacion: valoresVacios(CAMPOS_ESTABILIZACION_ANEXO2),
    custodia: valoresVacios(CAMPOS_CUSTODIA_ANEXO2),
    descargo: estadoInicialTablaConColumnas(FILAS_DESCARGO_ANEXO2, COLUMNAS_DESCARGO_ANEXO2),
    observaciones: valoresVacios(CAMPOS_OBSERVACIONES_ANEXO2),
    acompanante: valoresVacios(CAMPOS_ACOMPANANTE_ANEXO2),
    equipo: estadoInicialTablaConColumnas(FILAS_EQUIPO_ANEXO2, COLUMNAS_PERSONAL_ANEXO2),
    radio: estadoInicialTablaConColumnas(FILAS_RADIO_ANEXO2, COLUMNAS_PERSONAL_ANEXO2),
    unidad: valoresVacios(CAMPOS_UNIDAD_ANEXO2),
    destino: valoresVacios(CAMPOS_DESTINO_ANEXO2),
    exoneracion: valoresVacios(CAMPOS_EXONERACION_ANEXO2),
  };
}

/** Un `Par` por fila del catálogo que tenga algún dato: "<bloque> — <fila>" → "col:valor · col:valor". */
function paresDeTabla(titulo: string, filas: readonly FilaTablaConColumnas[], columnas: readonly ColumnaTablaConColumnas[], estado: EstadoTablaConColumnas): Par[] {
  return filas.map((f) => ({ pregunta: `${titulo} — ${f.etiqueta}`, respuesta: resumenFilaTablaConColumnas(estado, f.clave, columnas) }));
}

/** Serializa A-S a `Par[]` (filtra respuestas vacías). */
export function paresDeAnexo2(e: EstadoAnexo2): Par[] {
  const prefijo = (titulo: string): string => `${titulo} — `;
  const T = TITULOS_ANEXO2;
  const pares: Par[] = [
    ...paresDeCampos(CAMPOS_AMBULANCIA_ANEXO2, e.ambulancia, prefijo(T.ambulancia)),
    ...paresDeCampos(CAMPOS_TIPO_ATENCION_ANEXO2, e.tipoAtencion, prefijo(T.generales)),
    ...paresDeCampos(CAMPOS_GENERALES_ANEXO2, e.generales, prefijo(T.generales)),
    ...paresDeCampos(CAMPOS_INTERROGATORIO_ANEXO2, e.interrogatorio, prefijo(T.interrogatorio)),
  ];
  e.tomas.forEach((toma, i) => {
    const total = totalGlasgowAnexo2(toma);
    pares.push(...paresDeCampos(CAMPOS_TOMA_ANEXO2, toma, `${T.constantes} — toma ${i + 1} — `));
    if (total !== null) pares.push({ pregunta: `${T.constantes} — toma ${i + 1} — Glasgow total (/15)`, respuesta: String(total) });
  });
  pares.push(
    ...paresDeCampos(CAMPOS_PUPILAS_ANEXO2, e.pupilas, prefijo(`${T.constantes} — examen pupilar`)),
    ...paresDeCampos(CAMPOS_EXAMEN_ANEXO2, e.examen, prefijo(T.examen)),
    ...e.diagnosticos.map((d, i) => ({ pregunta: `${T.diagnostico} — ${i + 1}`, respuesta: conCodigoCie(d) })),
  );
  for (const g of GRUPOS_PROCEDIMIENTOS_ANEXO2) pares.push(...paresDeCampos(g.campos, e.procedimientos[g.clave], prefijo(`${T.procedimientos} — ${g.titulo}`)));
  for (const f of FILAS_MEDICACION_ANEXO2) {
    pares.push({ pregunta: `${T.procedimientos} — Medicación ${f.etiqueta}`, respuesta: resumenFilaTablaConColumnas(e.medicacion, f.clave, COLUMNAS_MEDICACION_ANEXO2) });
  }
  pares.push(
    ...paresDeCampos(CAMPOS_ORIGEN_TRAUMA_ANEXO2, e.origenTrauma, prefijo(T.trauma)),
    ...paresDeCampos(CAMPOS_TRANSITO_ANEXO2, e.transito, prefijo(`${T.trauma} — accidentes de tránsito`)),
    ...paresDeCampos(CAMPOS_OTROS_TRAUMA_ANEXO2, e.otrosTrauma, prefijo(T.trauma)),
  );
  e.lesiones.forEach((l, i) => {
    const partes = CAMPOS_LESION_ANEXO2.map((c) => textoDeCampo(c, l[c.clave])).filter((x) => x !== '');
    if (partes.length > 0) pares.push({ pregunta: `${T.trauma} — localización de lesión ${i + 1}`, respuesta: partes.join(' · ') });
  });
  pares.push(
    ...paresDeCampos(CAMPOS_GINECO_ANEXO2, e.gineco, prefijo(T.gineco)),
    ...paresDeTabla(`${T.gineco} — contracciones uterinas`, FILAS_CONTRACCIONES_ANEXO2, COLUMNAS_CONTRACCIONES_ANEXO2, e.contracciones),
    ...paresDeCampos(CAMPOS_PARO_ANEXO2, e.paro, prefijo(T.paro)),
    ...paresDeCampos(CAMPOS_ESTABILIZACION_ANEXO2, e.estabilizacion, prefijo(T.estabilizacion)),
    ...paresDeCampos(CAMPOS_CUSTODIA_ANEXO2, e.custodia, prefijo(T.custodia)),
    ...paresDeTabla(T.descargo, FILAS_DESCARGO_ANEXO2, COLUMNAS_DESCARGO_ANEXO2, e.descargo),
    ...paresDeCampos(CAMPOS_OBSERVACIONES_ANEXO2, e.observaciones, prefijo(T.observaciones)),
    ...paresDeCampos(CAMPOS_ACOMPANANTE_ANEXO2, e.acompanante, prefijo(T.acompanante)),
    ...paresDeTabla(T.equipo, FILAS_EQUIPO_ANEXO2, COLUMNAS_PERSONAL_ANEXO2, e.equipo),
    ...paresDeTabla(T.radio, FILAS_RADIO_ANEXO2, COLUMNAS_PERSONAL_ANEXO2, e.radio),
    ...paresDeCampos(CAMPOS_UNIDAD_ANEXO2, e.unidad, prefijo(`${T.radio} — datos de la unidad`)),
    ...paresDeCampos(CAMPOS_DESTINO_ANEXO2, e.destino, prefijo(T.destino)),
    ...paresDeCampos(CAMPOS_EXONERACION_ANEXO2, e.exoneracion, prefijo(T.exoneracion)),
  );
  return pares.filter((x) => x.respuesta.trim() !== '');
}
